import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { UserState } from '@gea/digital-ui-lib';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import { Observable, Subscriber } from 'rxjs';
import { ENVIRONMENT_CONFIG, EnvironmentConfiguration } from '@environments/models/environment.model';
import { ReportStatus } from '@shared/models/common.models';
import { MachineTriggerReportRequest } from '@generated-apis/model/machineTriggerReportRequest';
import {
  ReportGenerationFailedError,
  ReportStreamConnection,
  ReportStreamEvent,
  ReportStreamEventType,
  ReportStreamOpenError,
} from './report-stream.models';

@Injectable({
  providedIn: 'root',
})
export class ReportStreamService {
  private static readonly MAX_TRANSPORT_RETRIES = 3;

  private readonly store = inject(Store);
  private readonly environment = inject(ENVIRONMENT_CONFIG) as unknown as EnvironmentConfiguration;

  openStream(geaPUID: string, request: MachineTriggerReportRequest): Observable<ReportStreamEvent> {
    return new Observable<ReportStreamEvent>((subscriber) => {
      const controller = new AbortController();
      const connection: ReportStreamConnection = { terminated: false, transportRetries: 0 };

      fetchEventSource(this.buildStreamUrl(geaPUID), {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(request),
        signal: controller.signal,
        openWhenHidden: true,
        onopen: (response) => this.handleOpen(response, connection),
        onmessage: (message) => this.handleMessage(message, subscriber, connection),
        onclose: () => this.handleClose(connection),
        onerror: (error) => this.handleError(error, connection),
      })
        .then(() => {
          if (!connection.terminated) {
            subscriber.error(new Error('Report stream closed before completing'));
          }
        })
        .catch((error) => {
          if (!connection.terminated) {
            subscriber.error(error);
          }
        });

      return () => controller.abort();
    });
  }

  private buildStreamUrl(geaPUID: string): string {
    return `${this.environment.apisBaseUrl.insightPartner}/api/reports/machine/${geaPUID}/generate/stream`;
  }

  private buildHeaders(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.store.selectSnapshot(UserState.token)}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
  }

  private async handleOpen(response: Response, connection: ReportStreamConnection): Promise<void> {
    if (!response.ok) {
      throw new ReportStreamOpenError(response.status);
    }

    connection.transportRetries = 0;
  }

  private handleMessage(
    message: EventSourceMessage,
    subscriber: Subscriber<ReportStreamEvent>,
    connection: ReportStreamConnection
  ): void {
    switch (message.event) {
      case ReportStreamEventType.Status:
        subscriber.next({ type: ReportStreamEventType.Status, status: message.data as ReportStatus });
        break;
      case ReportStreamEventType.Complete:
        connection.terminated = true;
        subscriber.next({ type: ReportStreamEventType.Complete, downloadPath: message.data });
        subscriber.complete();
        break;
      case ReportStreamEventType.Error:
        throw new ReportGenerationFailedError(message.data || 'Report generation failed');
    }
  }

  private handleClose(connection: ReportStreamConnection): void {
    if (!connection.terminated) {
      throw new Error('Report stream closed before completing');
    }
  }

  private handleError(error: unknown, connection: ReportStreamConnection): void {
    if (
      error instanceof ReportStreamOpenError ||
      error instanceof ReportGenerationFailedError ||
      connection.transportRetries >= ReportStreamService.MAX_TRANSPORT_RETRIES
    ) {
      throw error;
    }

    connection.transportRetries += 1;
  }
}
